export interface VehiclePhotos {
  frontal?: string | null;
  trasera?: string | null;
  lateralIzq?: string | null;
  lateralDer?: string | null;
  tablero?: string | null;
  refaccion?: string | null;
  herramienta?: string | null;
  // Fotos adicionales por tipo de vehículo (vehículos legados no las usan).
  interiorFrontal?: string | null;
  interiorTrasero?: string | null;
  caja?: string | null;
  filaDelantera?: string | null;
  filaTrasera?: string | null;
}
export const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 1024;
        let width = img.width;
        let height = img.height;
        if (width > MAX) {
          height = (height * MAX) / width;
          width = MAX;
        }
        if (height > MAX) {
          width = (width * MAX) / height;
          height = MAX;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Mismo redimensionado que compressImage pero devolviendo un Blob binario:
// nunca materializa el string base64 gigante (el +33% y el heap son lo que
// revienta navegadores de gama baja con ~20 fotos acumuladas).
export const compressImageToBlob = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 1024;
        let width = img.width;
        let height = img.height;
        if (width > MAX) {
          height = (height * MAX) / width;
          width = MAX;
        }
        if (height > MAX) {
          width = (width * MAX) / height;
          height = MAX;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('No blob'))),
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Flujo nuevo de captura: comprime, sube INMEDIATAMENTE al bucket y devuelve
// la URL pública — en el estado de los modales sólo queda un string chico.
// Flujo nuevo de captura: comprime, sube INMEDIATAMENTE al bucket y devuelve
// la URL pública — en el estado de los modales sólo queda un string chico.
// DataService se importa DENTRO de la función (no top-level): este archivo lo
// ejecutan tests unitarios y un import de módulo arrastraría axios/antd al
// entorno de jest y rompería el suite.
export const uploadFotoCapturada = async (file: File): Promise<string> => {
  const { DataService } = await import('../../../services/http-interceptor.service');
  const blob = await compressImageToBlob(file);
  const form = new FormData();
  form.append('imagen', blob, 'foto.jpg');
  const res: any = await DataService.uploadImagen(form);
  return res?.data?.url;
};

export const PHOTO_SLOTS: Array<{ key: keyof VehiclePhotos; label: string }> = [
  { key: 'frontal', label: 'Frontal' },
  { key: 'trasera', label: 'Trasera' },
  { key: 'lateralIzq', label: 'Lateral izquierdo' },
  { key: 'lateralDer', label: 'Lateral derecho' },
  { key: 'tablero', label: 'Tablero' },
  { key: 'refaccion', label: 'Refacción' },
  { key: 'herramienta', label: 'Herramienta' },
];

export const isEvidenceComplete = (
  photos: VehiclePhotos | null | undefined,
  firma: string | null | undefined,
  tipoVehiculo?: string | null
): boolean => {
  if (!photos || !firma) return false;
  return photoMarkersPorTipo(tipoVehiculo).every((slot) => !!photos[slot.key]);
};

export const ACCESORIOS_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'espejoLateralDerecho', label: 'Espejo lateral derecho' },
  { key: 'espejoLateralIzquierdo', label: 'Espejo lateral izquierdo' },
  { key: 'espejoRetrovisor', label: 'Espejo retrovisor' },
  { key: 'tapetes', label: 'Tapetes' },
  { key: 'limpiadores', label: 'Limpiadores' },
  { key: 'claxon', label: 'Claxon' },
  { key: 'viseras', label: 'Viseras' },
  { key: 'cinturonesSeguridad', label: 'Cinturones de seguridad' },
  { key: 'clima', label: 'Clima' },
  { key: 'manijas', label: 'Manijas' },
  { key: 'radio', label: 'Radio' },
  { key: 'parabrisas', label: 'Parabrisas' },
  { key: 'medallonTrasero', label: 'Medallón trasero' },
  { key: 'cristalesPuertas', label: 'Cristales de puertas (laterales)' },
  { key: 'farosYLuces', label: 'Faros y luces' },
  { key: 'molduras', label: 'Molduras' },
  { key: 'calaveras', label: 'Calaveras' },
  { key: 'defensas', label: 'Defensas' },
  { key: 'parrilla', label: 'Parrilla' },
  { key: 'taponGasolina', label: 'Tapón de gasolina' },
  { key: 'taponRadiador', label: 'Tapón de radiador' },
  { key: 'taponAceite', label: 'Tapón de aceite' },
  { key: 'bayonetaAceite', label: 'Bayoneta de aceite' },
  { key: 'llaveCruz', label: 'Llave de cruz' },
  { key: 'gato', label: 'Gato' },
  { key: 'reflejantesEmergencia', label: 'Reflejantes de emergencia (señalamientos)' },
  { key: 'extinguidor', label: 'Extinguidor' },
  { key: 'cablesCorriente', label: 'Cables de corriente' },
  { key: 'cajaHerramientas', label: 'Caja de herramientas' },
  { key: 'portaLlantas', label: 'Porta llantas' },
  { key: 'placaDelantera', label: 'Placa delantera' },
  { key: 'placaTrasera', label: 'Placa trasera' },
  { key: 'bolsasAire', label: 'Bolsas de aire' },
];

export const CARROCERIA_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'costadoDerecho', label: 'Costado derecho' },
  { key: 'costadoIzquierdo', label: 'Costado izquierdo' },
  { key: 'cofre', label: 'Cofre' },
  { key: 'toldo', label: 'Toldo' },
  { key: 'exteriorLimpio', label: 'Exterior limpio' },
  { key: 'cajuela', label: 'Cajuela' },
  { key: 'pintura', label: 'Pintura' },
  { key: 'sistemaAlarma', label: 'Sistema de alarma' },
  { key: 'vestiduras', label: 'Vestiduras' },
  { key: 'limpiezaInterior', label: 'Limpieza interior' },
];

export const LLANTAS_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'delanteraDerecha', label: 'Delantera derecha' },
  { key: 'delanteraIzquierda', label: 'Delantera izquierda' },
  { key: 'traseraDerecha', label: 'Trasera derecha' },
  { key: 'traseraIzquierda', label: 'Trasera izquierda' },
  { key: 'refaccion', label: 'Refacción' },
];

export const CONDICION_CARROCERIA_OPTIONS = [
  { value: 'bueno', label: 'Bueno' },
  { value: 'regular', label: 'Regular' },
  { value: 'malo', label: 'Malo' },
];

export const CONDICION_LLANTA_OPTIONS = [
  { value: 'nueva', label: 'Nueva' },
  { value: 'media_vida', label: '½ Vida' },
  { value: 'cuarto_vida', label: '¼ Vida' },
  { value: 'lisa', label: 'Lisa' },
];

export const COMBUSTIBLE_OPTIONS = [
  { value: 'E', label: 'E' },
  { value: '1/4', label: '¼' },
  { value: '1/2', label: '½' },
  { value: '3/4', label: '¾' },
  { value: 'F', label: 'F' },
];

export const LINEAMIENTOS: string[] = [
  'En el caso de que se soliciten recursos para la carga de combustible, deberá anexar el comprobante de pago a la sección de gastos del sistema.',
  'Al recibir el vehículo me comprometo a no conducirlo después de haber ingerido bebidas alcohólicas y/o drogas no autorizadas, ni haber tomado medicamentos que puedan generar somnolencia o mareos; dispongo de licencia de conducir vigente y emplearé el celular únicamente de localización con portacelular del vehículo mientras conduzco.',
  'Emplear el vehículo únicamente para las actividades propias de la empresa y no para asuntos de carácter personal.',
  'Al concluir la actividad para la cual me fue facilitado el vehículo, lo devolveré a la empresa (no a mi domicilio particular), sin dejar basura ni suciedad e informaré de cualquier falla observada.',
  'En caso de necesitar el vehículo antes del inicio de las horas laborales, deberé coordinarme con el área responsable de las llaves.',
  'Cuando la devolución se realice fuera de los horarios laborales, dejaré las llaves en un lugar visible y de fácil acceso.',
  'En caso de que durante el empleo del vehículo sea objeto de alguna infracción y/o sanción por parte de las autoridades, informaré por la vía más rápida para determinar las acciones a realizar.',
  'Cualquier infracción económica deberá ser cubierta por el conductor del vehículo, siempre y cuando sea atribuible por el mal uso por parte del conductor.',
  'Los daños que se generen al vehículo serán informados para su revisión y si son atribuibles al mal uso del vehículo serán cubiertos por el conductor responsable.',
];

export const defaultAccesorios = (): Record<string, boolean> =>
  Object.fromEntries(ACCESORIOS_ITEMS.map((item) => [item.key, true]));

export const defaultCarroceria = (): Record<string, string> =>
  Object.fromEntries(CARROCERIA_ITEMS.map((item) => [item.key, 'bueno']));

export const defaultLlantas = (): Record<string, string> =>
  Object.fromEntries(LLANTAS_ITEMS.map((item) => [item.key, 'nueva']));

export const isChecklistStepComplete = (
  kilometraje: number | null | undefined,
  combustible: string | null | undefined,
  // Fotos del combustible: se exigen ≥1 en cada tramo (reunión de vehículos).
  fotosCombustible: number = 0
): boolean => {
  return (
    typeof kilometraje === 'number' &&
    kilometraje >= 0 &&
    !!combustible &&
    fotosCombustible >= 1
  );
};


// Llantas se retiró del checklist: el prefill sólo requiere accesorios y
// carrocería (evidencias viejas con llantas siguen cargando, sin problema).
export interface ChecklistPrefillSource {
  accesorios?: Record<string, boolean> | null;
  carroceria?: Record<string, string> | null;
  // Nivel con el que quedó el vehículo (última evidencia): etiqueta "actual"
  // de la salida. El alta no lo registra → null.
  combustible?: string | null;
  // TAG reportado en la salida de ESTE préstamo: la devolución parte de él
  // (los cambios durante el uso persisten entre tramos). Null = usa catálogo.
  tagCuenta?: boolean | null;
  tagSerie?: string | null;
  origen?: 'checkin' | 'checkout' | 'alta' | null;
  fecha?: string | Date | null;
  folio?: string | null;
}

export interface ChecklistPrefill {
  accesorios: Record<string, boolean>;
  carroceria: Record<string, string>;
  combustible: string | null;
  origen: 'checkin' | 'checkout' | 'alta';
  fecha: string | Date;
  folio: string;
}

export const resolveChecklistPrefill = (
  source: ChecklistPrefillSource | null | undefined
): ChecklistPrefill | null => {
  if (!source) return null;
  if (!source.accesorios || !source.carroceria) return null;
  if (!source.origen || !source.fecha || !source.folio) return null;
  return {
    accesorios: source.accesorios,
    carroceria: source.carroceria,
    combustible: source.combustible ?? null,
    origen: source.origen,
    fecha: source.fecha,
    folio: source.folio,
  };
};

export const formatPrefillDate = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const VEHICLE_COLORS = {
  navy: '#232a5e',
  textPrimary: '#1b1d29',
  textSecondary: '#6b6e7e',
  textTertiary: '#8b8e9c',
  fieldLabel: '#3c3f4e',
  appBg: '#f5f5f7',
  pageBg: '#e9eaef',
  surface: '#fff',
  cardBorder: '#e3e4ea',
  inputBorder: '#d9dbe3',
  rowDivider: '#f0f1f5',
  segmentTrack: '#eef0f4',
  badgeEnUsoBg: '#fdf3e0',
  badgeEnUsoText: '#9a6700',
  badgePorRevisarBg: '#e7edfb',
  badgePorRevisarText: '#2b4bab',
  badgeDisponibleBg: '#e5f3e9',
  badgeDisponibleText: '#1a7f37',
  badgeRechazadaBg: '#fbe9e9',
  badgeRechazadaText: '#9a1f1f',
  badgeServicioBg: '#f9f0ff',
  badgeServicioText: '#722ed1',
  success: '#1a7f37',
  warningText: '#9a6700',
  missingText: '#9a1f1f',
  missingBorder: '#dca3a3',
  lineamientosAcceptedBg: '#eef0f8',
  cameraSheetMask: 'rgba(15,17,35,.55)',
  diagramStroke: '#b8bccb',
  diagramGlass: '#c9ccd8',
  photoPendingBorder: '#c3c6d1',
  photoCapturedBg: '#f4faf5',
};

export interface PhotoMarker {
  key: keyof VehiclePhotos;
  label: string;
  number: number;
  x: number;
  y: number;
}

export const PHOTO_MARKERS: PhotoMarker[] = [
  { key: 'frontal', label: 'Frontal', number: 1, x: 50, y: 4 },
  { key: 'trasera', label: 'Trasera', number: 2, x: 50, y: 96 },
  { key: 'lateralIzq', label: 'Lateral izquierdo', number: 3, x: 10, y: 50 },
  { key: 'lateralDer', label: 'Lateral derecho', number: 4, x: 90, y: 50 },
  { key: 'tablero', label: 'Tablero', number: 5, x: 50, y: 36 },
  { key: 'refaccion', label: 'Refacción', number: 6, x: 38, y: 86 },
  { key: 'herramienta', label: 'Herramienta', number: 7, x: 62, y: 86 },
];

// Clasificación del vehículo: define qué fotos adicionales pide el wizard.
// Debe espejar FOTOS_POR_TIPO del backend (api-tecnocom vehicleChecklist.js).
export type VehicleTipo = 'camioneta' | 'pickup' | 'doble_cabina';

const FOTO_KEYS_POR_TIPO: Record<VehicleTipo, Array<keyof VehiclePhotos>> = {
  camioneta: ['interiorFrontal', 'interiorTrasero'],
  pickup: ['interiorFrontal', 'interiorTrasero', 'caja'],
  doble_cabina: ['interiorFrontal', 'interiorTrasero', 'caja', 'filaDelantera', 'filaTrasera'],
};

const EXTRA_LABELS: Record<string, string> = {
  interiorFrontal: 'Interior frontal',
  interiorTrasero: 'Interior trasero',
  caja: 'Caja',
  filaDelantera: 'Fila delantera',
  filaTrasera: 'Fila trasera',
};

// Posiciones sobre el diagrama (porcentaje del contenedor). La base de 7 está
// pineada por vehicleEvidenceSelectors.test.ts; las extras continúan la
// numeración a partir del 8 y sus posiciones son ajustables visualmente.
const EXTRA_MARKERS: Record<string, { number: number; x: number; y: number }> = {
  interiorFrontal: { number: 8, x: 42, y: 42 },
  interiorTrasero: { number: 9, x: 42, y: 60 },
  caja: { number: 10, x: 50, y: 80 },
  filaDelantera: { number: 11, x: 58, y: 42 },
  filaTrasera: { number: 12, x: 58, y: 60 },
};

export const isVehicleTipo = (value: unknown): value is VehicleTipo =>
  value === 'camioneta' || value === 'pickup' || value === 'doble_cabina';

// Daños preexistentes nombrados en el alta del vehículo (rayones, golpes,
// vidrios rotos, fallas mecánicas). Debe espejar ZONAS_DANIO/TIPOS_DANIO/
// SEVERIDADES_DANIO del backend (api-tecnocom vehicleChecklist.js).
export interface DanioVehiculo {
  _id?: string;
  zona: string;
  tipo: string;
  severidad: string;
  descripcion?: string;
  origen?: string;
  resuelto?: boolean;
  notaResolucion?: string;
}

// Daños vigentes (los resueltos ya no advierten nada al conductor).
export const danosVigentesDe = (vehiculo?: { danos?: DanioVehiculo[] | null } | null): DanioVehiculo[] =>
  (vehiculo?.danos || []).filter((dano) => !dano.resuelto);

// Fotos de evidencia del estado en que se recibió el vehículo al alta
// (estadoInicial.fotos, URLs GCS); legados sin captura → [].
export const fotosEstadoInicialDe = (vehiculo?: { estadoInicial?: { fotos?: string[] | null } | null } | null): string[] => {
  const fotos = vehiculo?.estadoInicial?.fotos;
  return Array.isArray(fotos) ? fotos : [];
};

export const ZONA_DANIO_LABELS: Record<string, string> = {
  cofre: 'Cofre',
  toldo: 'Toldo',
  costadoDerecho: 'Costado derecho',
  costadoIzquierdo: 'Costado izquierdo',
  cajuela: 'Cajuela',
  parabrisas: 'Parabrisas',
  cristalesLaterales: 'Cristales laterales',
  medallonTrasero: 'Medallón trasero',
  farosLuces: 'Faros y luces',
  defensas: 'Defensas',
  interior: 'Interior',
  frenos: 'Frenos',
  direccion: 'Dirección',
  suspension: 'Suspensión',
  motor: 'Motor',
  transmision: 'Transmisión',
  otra: 'Otra',
};

export const TIPO_DANIO_LABELS: Record<string, string> = {
  rayon: 'Rayón',
  golpe: 'Golpe',
  vidrio_roto: 'Vidrio/cristal roto',
  falla_mecanica: 'Falla mecánica',
  otra: 'Otra',
};

export const SEVERIDAD_DANIO_LABELS: Record<string, string> = {
  leve: 'Leve',
  moderada: 'Moderada',
  grave: 'Grave',
};

/**
 * Marcadores del wizard de fotos para un tipo de vehículo: la base 7 más las
 * que agrega el tipo. Sin tipo (vehículo legado sin clasificar) = solo base.
 */
export const photoMarkersPorTipo = (tipo?: string | null): PhotoMarker[] => {
  if (!isVehicleTipo(tipo)) return PHOTO_MARKERS;
  const extras = FOTO_KEYS_POR_TIPO[tipo].map((key) => ({
    key,
    label: EXTRA_LABELS[key as string],
    ...EXTRA_MARKERS[key as string],
  }));
  return [...PHOTO_MARKERS, ...extras];
};

export const ESTADO_LOAN_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  SOLICITADA: { bg: VEHICLE_COLORS.badgeEnUsoBg, text: VEHICLE_COLORS.badgeEnUsoText, label: 'Solicitado' },
  RECHAZADA: { bg: VEHICLE_COLORS.badgeRechazadaBg, text: VEHICLE_COLORS.badgeRechazadaText, label: 'Rechazado' },
  ASIGNADO: { bg: VEHICLE_COLORS.badgeEnUsoBg, text: VEHICLE_COLORS.badgeEnUsoText, label: 'Pendiente' },
  ACTIVO: { bg: VEHICLE_COLORS.badgeEnUsoBg, text: VEHICLE_COLORS.badgeEnUsoText, label: 'En uso' },
  POR_REVISAR: { bg: VEHICLE_COLORS.badgePorRevisarBg, text: VEHICLE_COLORS.badgePorRevisarText, label: 'Por revisar' },
  FINALIZADO: { bg: VEHICLE_COLORS.badgeDisponibleBg, text: VEHICLE_COLORS.badgeDisponibleText, label: 'Devuelto' },
  CANCELADO: { bg: VEHICLE_COLORS.appBg, text: VEHICLE_COLORS.textTertiary, label: 'Cancelado' },
};

export const ESTADO_VEHICULO_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  disponible: { bg: VEHICLE_COLORS.badgeDisponibleBg, text: VEHICLE_COLORS.badgeDisponibleText, label: 'Disponible' },
  solicitado: { bg: VEHICLE_COLORS.badgeEnUsoBg, text: VEHICLE_COLORS.badgeEnUsoText, label: 'Solicitado' },
  esperando_salida: { bg: VEHICLE_COLORS.badgePorRevisarBg, text: VEHICLE_COLORS.badgePorRevisarText, label: 'Esperando salida' },
  prestado: { bg: VEHICLE_COLORS.badgeEnUsoBg, text: VEHICLE_COLORS.badgeEnUsoText, label: 'En uso' },
  por_revisar: { bg: VEHICLE_COLORS.badgePorRevisarBg, text: VEHICLE_COLORS.badgePorRevisarText, label: 'Por revisar' },
  en_servicio: { bg: VEHICLE_COLORS.badgeServicioBg, text: VEHICLE_COLORS.badgeServicioText, label: 'En Mantenimiento' },
  baja: { bg: VEHICLE_COLORS.badgeRechazadaBg, text: VEHICLE_COLORS.badgeRechazadaText, label: 'Baja' },
  deshabilitado: { bg: VEHICLE_COLORS.badgeRechazadaBg, text: VEHICLE_COLORS.badgeRechazadaText, label: 'Deshabilitado' },
};

export const isSolicitudFormComplete = (
  objetivo: string | null | undefined,
  fechaInicioUso: { valueOf(): number } | null | undefined,
  fechaFinUso: { valueOf(): number } | null | undefined
): boolean => {
  if (!objetivo || !objetivo.trim()) return false;
  if (!fechaInicioUso || !fechaFinUso) return false;
  // Día calendario: el DatePicker manda medianoche local, se compara contra
  // el inicio de hoy (no Date.now()) para que "hoy" elegido por la tarde pase.
  const hoyInicio = new Date().setHours(0, 0, 0, 0);
  if (Number(fechaInicioUso.valueOf()) < hoyInicio) return false;
  return Number(fechaFinUso.valueOf()) >= Number(fechaInicioUso.valueOf());
};

// --- Geolocalización de fotos -------------------------------------------------
// La posición se toma del DISPOSITIVO al capturar (el canvas borra el EXIF
// de la foto original, así que es la única vía confiable). Si el permiso se
// niega, no está disponible o tarda: null — la foto NUNCA se bloquea.

export type Coords = { lat: number; lng: number };

export const getCurrentCoords = (): Promise<Coords | null> =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      // maximumAge generoso: la posición caché del dispositivo es suficiente
      // (la persona está junto al vehículo), y evita esperar un fix GPS frío.
      { timeout: 8000, maximumAge: 120000 }
    );
  });

// El backend guarda cada foto como string (legado, sin coordenadas) o como
// {url, lat, lng}. Las vistas NUNCA leen la forma cruda: siempre por acá.
export type FotoGeo = string | { url: string; lat?: number | null; lng?: number | null };

export const fotoUrl = (foto: FotoGeo | null | undefined): string | null => {
  if (!foto) return null;
  return typeof foto === 'string' ? foto : foto.url || null;
};

export const fotoCoords = (foto: FotoGeo | null | undefined): Coords | null => {
  if (!foto || typeof foto === 'string') return null;
  if (foto.lat == null || foto.lng == null) return null;
  return { lat: foto.lat, lng: foto.lng };
};

export const coordsLabel = (coords: Coords | null): string | null =>
  coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : null;

export const mapsLink = (coords: Coords | null): string | null =>
  coords ? `https://maps.google.com/?q=${coords.lat},${coords.lng}` : null;

// Serializa una foto capturada (data URL/URL + coords opcionales) al shape
// que espera la API: objeto geo si hay coordenadas, string si no.
export const fotoPayload = (img: string, coords: Coords | null): FotoGeo =>
  coords ? { url: img, lat: coords.lat, lng: coords.lng } : img;

export const mostRecentRejection = <T extends { estado?: string }>(
  history: T[] | null | undefined
): T | null => {
  if (!history || !history.length) return null;
  return history[0].estado === 'RECHAZADA' ? history[0] : null;
};
